import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { getPosition, geoErrorMessage } from "../../lib/geo";
import { PageHeader, EmptyState } from "../../components/app/Common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { AddressAutocomplete } from "../../components/app/AddressAutocomplete";
import LocationMap from "../../components/LocationMap";
import { Loader2, LocateFixed, MapPin, Minus, Pencil, Plus, PlusCircle, Trash2 } from "lucide-react";

/* ---------- raio da cerca (geofence) ---------- */

const MIN_R = 10;
const MAX_R = 5000;
const DEFAULT_R = 200;
const PRESETS = [50, 100, 200, 300, 500, 1000];
const TICKS = [100, 500, 1000, 5000]; // [ajuste-visual] sem o 10 m, que ficava em cima do 100 m

// Valores que o controle deslizante percorre: de 10 em 10 m ate 500 m (onde se mexe mais),
// depois passos maiores. Assim o ajuste fino fica facil nos raios comuns.
const LADDER = (() => {
  const v = [];
  for (let r = 10; r <= 500; r += 10) v.push(r);
  for (let r = 525; r <= 1000; r += 25) v.push(r);
  for (let r = 1050; r <= 2500; r += 50) v.push(r);
  for (let r = 2600; r <= 5000; r += 100) v.push(r);
  return v;
})();

function clampRadius(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return DEFAULT_R;
  return Math.min(MAX_R, Math.max(MIN_R, n));
}

function nearestIndex(r) {
  let best = 0;
  for (let i = 1; i < LADDER.length; i += 1) {
    if (Math.abs(LADDER[i] - r) < Math.abs(LADDER[best] - r)) best = i;
  }
  return best;
}

function stepUp(r) {
  const next = LADDER.find((v) => v > r);
  return next === undefined ? MAX_R : next;
}

function stepDown(r) {
  for (let i = LADDER.length - 1; i >= 0; i -= 1) {
    if (LADDER[i] < r) return LADDER[i];
  }
  return MIN_R;
}

function fmtRadius(r) {
  if (r < 1000) return `${r} m`;
  return `${String(r / 1000).replace(/\.0+$/, "")} km`;
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Controle do raio: mapa com o circulo ao vivo, numero grande com - e +, atalhos e um
// controle deslizante com marcas de distancia.
function RadiusControl({ value, onChange, hasLocation, lat, lng, name }) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText(String(value));
  }, [value]);
  const previewRadius = useDebounced(value, 150);
  const idx = nearestIndex(value);

  const typed = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    setText(digits);
    const n = Number(digits);
    if (digits && n >= MIN_R && n <= MAX_R) onChange(n);
  };

  const finishTyping = () => {
    const n = text ? clampRadius(text) : value;
    onChange(n);
    setText(String(n));
  };

  const tip =
    value < 50
      ? { warn: true, text: "Very small. Phone GPS is often off by 10 to 30 m near buildings, so people on site may be blocked. 100 m or more is safer." }
      : value > 1000
      ? { warn: true, text: "Very large. People could clock in from far away." }
      : { warn: false, text: `Anyone within ${fmtRadius(value)} of the jobsite can clock in.` };

  return (
    <div className="space-y-3" data-testid="radius-control">
      {hasLocation ? (
        <LocationMap user={null} site={{ lat, lng, radius_m: previewRadius, name: name || "Jobsite" }} height={210} />
      ) : (
        <div
          className="grid place-items-center rounded-lg border border-dashed border-border bg-muted/30 px-6 text-center text-sm text-muted-foreground"
          style={{ height: 210 }}
          data-testid="radius-map-placeholder"
        >
          Pick an address, or use your current location, to see the geofence on the map.
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onChange(stepDown(value))}
          disabled={value <= MIN_R}
          aria-label="Decrease radius"
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-border active:bg-accent disabled:opacity-40"
          data-testid="js-radius-minus"
        >
          <Minus className="h-5 w-5" />
        </button>

        <div className="flex flex-1 items-baseline justify-center gap-1">
          <input
            type="text"
            inputMode="numeric"
            value={text}
            onChange={(e) => typed(e.target.value)}
            onBlur={finishTyping}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            aria-label="Radius in meters"
            className="bg-transparent text-center text-3xl font-bold outline-none"
            style={{ width: `${Math.max(2, text.length) + 0.6}ch` }}
            data-testid="js-radius-input"
          />
          <span className="text-lg font-medium text-muted-foreground">m</span>
        </div>

        <button
          type="button"
          onClick={() => onChange(stepUp(value))}
          disabled={value >= MAX_R}
          aria-label="Increase radius"
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-border active:bg-accent disabled:opacity-40"
          data-testid="js-radius-plus"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>

      <div>
        <input
          type="range"
          min={0}
          max={LADDER.length - 1}
          step={1}
          value={idx}
          onChange={(e) => onChange(LADDER[Number(e.target.value)])}
          aria-label="Radius slider"
          className="h-10 w-full cursor-pointer accent-[hsl(var(--primary))]"
          data-testid="js-radius"
        />
        <div className="relative h-4 text-[10px] text-muted-foreground">
          {TICKS.map((t, i) => {
            const pct = (LADDER.indexOf(t) / (LADDER.length - 1)) * 100;
            const shift = i === 0 ? "" : i === TICKS.length - 1 ? "-translate-x-full" : "-translate-x-1/2";
            return (
              <span key={t} className={`absolute top-0 whitespace-nowrap ${shift}`} style={{ left: `${pct}%` }}>
                {fmtRadius(t)}
              </span>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-pressed={value === p}
            className={`h-10 rounded-full border px-4 text-sm font-semibold ${
              value === p ? "border-primary bg-primary text-black" : "border-border text-muted-foreground active:bg-accent"
            }`}
            data-testid={`js-radius-preset-${p}`}
          >
            {fmtRadius(p)}
          </button>
        ))}
      </div>

      <p
        className={`rounded-lg px-3 py-2 text-xs ${tip.warn ? "border border-amber-500/40 bg-amber-500/10 text-amber-500" : "bg-muted/50 text-muted-foreground"}`}
        data-testid="radius-tip"
      >
        {tip.text}
      </p>
    </div>
  );
}

/* ---------- pagina ---------- */

const emptyForm = { name: "", address: "", lat: 0, lng: 0, radius_m: DEFAULT_R, team_ids: [], links: [] };
const fieldClass = "h-11 rounded-lg text-base md:h-9 md:rounded-sm md:text-sm";

export default function AdminJobsites() {
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [coordText, setCoordText] = useState({ lat: "", lng: "" });
  const [editing, setEditing] = useState(null); // obra sendo editada (null = obra nova)
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [staff, setStaff] = useState([]); // [equipe-projeto] admins e supervisores que podem entrar na equipe

  const hasLocation = !!form.lat && !!form.lng;

  const load = () => api.get("/jobsites").then(({ data }) => setSites(data));
  useEffect(() => {
    load();
    api
      .get("/company/users")
      .then(({ data }) =>
        setStaff(
          (Array.isArray(data) ? data : [])
            .filter((u) => (u.role === "admin" || u.role === "supervisor") && !["pending", "worker_pending", "rejected", "deleted"].includes(u.status))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        )
      )
      .catch(() => {});
  }, []);

  const setPoint = (lat, lng) => {
    setForm((f) => ({ ...f, lat, lng }));
    setCoordText({ lat: String(Number(lat).toFixed(6)), lng: String(Number(lng).toFixed(6)) });
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setCoordText({ lat: "", lng: "" });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      address: s.address || "",
      lat: Number(s.lat) || 0,
      lng: Number(s.lng) || 0,
      radius_m: clampRadius(s.radius_m || DEFAULT_R),
      team_ids: Array.isArray(s.team_ids) ? s.team_ids : (s.team || []).map((t) => t.id),
      links: (s.links || []).map((l) => ({ title: l.title || "", url: l.url || "" })),
    });
    setCoordText({
      lat: s.lat ? String(Number(s.lat).toFixed(6)) : "",
      lng: s.lng ? String(Number(s.lng).toFixed(6)) : "",
    });
    setOpen(true);
  };

  const handleOpenChange = (o) => {
    if (!o && saving) return;
    setOpen(o);
    if (!o) setEditing(null);
  };

  const handleLocationSelect = ({ latitude, longitude, address }) => {
    setForm((f) => ({ ...f, address, lat: latitude, lng: longitude }));
    setCoordText({ lat: String(Number(latitude).toFixed(6)), lng: String(Number(longitude).toFixed(6)) });
    toast.success("📍 Location found!");
  };

  // Campos manuais: guardam o texto como a pessoa digita (assim da para escrever "-37.8").
  const typeCoord = (axis, raw) => {
    setCoordText((c) => ({ ...c, [axis]: raw }));
    const n = Number(raw.replace(",", "."));
    const limit = axis === "lat" ? 90 : 180;
    const valid = raw.trim() !== "" && Number.isFinite(n) && Math.abs(n) <= limit;
    setForm((f) => ({ ...f, [axis]: valid ? n : 0 }));
  };

  // [equipe-projeto] equipe e links do projeto
  const toggleTeam = (id) =>
    setForm((f) => ({ ...f, team_ids: f.team_ids.includes(id) ? f.team_ids.filter((x) => x !== id) : [...f.team_ids, id] }));
  const setLink = (i, key, value) => setForm((f) => ({ ...f, links: f.links.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)) }));
  const addLink = () => setForm((f) => (f.links.length >= 12 ? f : { ...f, links: [...f.links, { title: "", url: "" }] }));
  const removeLink = (i) => setForm((f) => ({ ...f, links: f.links.filter((_, idx) => idx !== i) }));

  const locateMe = async () => {
    setLocating(true);
    try {
      const p = await getPosition(10000);
      setPoint(p.lat, p.lng);
      toast.success(`📍 Location found (about ${Math.round(p.accuracy || 0)} m)`);
    } catch (e) {
      toast.error(geoErrorMessage(e));
    } finally {
      setLocating(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Enter a jobsite name");
      return;
    }
    if (!hasLocation) {
      toast.error("Select a location");
      return;
    }
    const payload = {
      name: form.name.trim(),
      address: form.address,
      lat: Number(form.lat) || 0,
      lng: Number(form.lng) || 0,
      radius_m: clampRadius(form.radius_m),
    };
    setSaving(true);
    let createdId = null;
    try {
      if (editing) {
        // O servidor troca todos os campos de uma vez: estado e CEP precisam ir junto para nao serem apagados.
        await api.patch(`/jobsites/${editing.id}`, {
          ...payload,
          state: editing.state || "",
          postcode: editing.postcode || "",
        });
        toast.success("✅ Jobsite updated");
      } else {
        const { data: created } = await api.post("/jobsites", payload);
        createdId = created && created.id;
        toast.success("✅ Jobsite created");
      }
      // [equipe-projeto] a equipe e os links ficam em rota propria; se falhar, a obra ja foi salva
      const savedId = editing ? editing.id : createdId;
      if (savedId) {
        try {
          await api.put(`/jobsites/${savedId}/info`, { team_ids: form.team_ids, links: form.links.filter((l) => l.url.trim()) });
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail, "The jobsite was saved, but the team and links were not."));
        }
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete jobsite?")) return;
    await api.delete(`/jobsites/${id}`);
    toast.success("Jobsite deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <PageHeader
        title="Jobsites"
        subtitle="Define sites with geofence coordinates for clock in/out validation."
        action={
          <Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="new-jobsite-btn">
            <PlusCircle className="h-4 w-4 mr-1.5" /> Add jobsite
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-xl md:max-w-lg md:rounded-sm">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit jobsite" : "New jobsite"}</DialogTitle>
            <DialogDescription>
              {editing ? "Change the details or the geofence of this jobsite." : "Set where the jobsite is and how far from it people can clock in."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Nome */}
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Downtown Site"
                className={fieldClass}
                data-testid="js-name"
              />
            </div>

            {/* Endereço com Autocomplete */}
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">Address *</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                onLocationSelect={handleLocationSelect}
              />
            </div>

            {/* Usar minha localização */}
            <Button
              type="button"
              variant="outline"
              onClick={locateMe}
              disabled={locating}
              className="h-12 w-full rounded-lg border-border text-base md:h-9 md:rounded-sm md:text-sm"
              data-testid="js-use-my-location"
            >
              {locating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LocateFixed className="mr-2 h-4 w-4" />}
              {locating ? "Finding your location..." : "Use my current location"}
            </Button>

            {/* Coordenadas (so quando ja existe um local) */}
            {hasLocation ? (
              <div className="space-y-1 rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="js-coords-box">
                <div className="font-mono text-xs text-primary">
                  📍 Latitude: <span className="font-bold">{Number(form.lat).toFixed(6)}</span>
                </div>
                <div className="font-mono text-xs text-primary">
                  📍 Longitude: <span className="font-bold">{Number(form.lng).toFixed(6)}</span>
                </div>
              </div>
            ) : null}

            {/* Inputs manuais (opcional) */}
            <details className="text-xs">
              <summary className="cursor-pointer py-1 text-muted-foreground hover:text-foreground">⚙️ Edit coordinates manually</summary>
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="mb-1 block text-xs">Latitude</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={coordText.lat}
                    onChange={(e) => typeCoord("lat", e.target.value)}
                    placeholder="e.g., -37.8043"
                    className={`${fieldClass} font-mono`}
                    data-testid="js-lat"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Longitude</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={coordText.lng}
                    onChange={(e) => typeCoord("lng", e.target.value)}
                    placeholder="e.g., 144.9667"
                    className={`${fieldClass} font-mono`}
                    data-testid="js-lng"
                  />
                </div>
              </div>
            </details>

            {/* Raio */}
            <div>
              <Label className="mb-2 block text-sm font-medium md:text-xs">Geofence radius *</Label>
              <RadiusControl
                value={form.radius_m}
                onChange={(r) => setForm((f) => ({ ...f, radius_m: r }))}
                hasLocation={hasLocation}
                lat={Number(form.lat)}
                lng={Number(form.lng)}
                name={form.name.trim()}
              />
            </div>

            {/* [equipe-projeto] Equipe do projeto: quem a turma ve (e pode ligar) na tela "Project information" */}
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">Project team</Label>
              <p className="mb-2 text-xs text-muted-foreground">Choose the admins and supervisors your crew can contact for this project.</p>
              {staff.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-muted-foreground" data-testid="js-team-empty">
                  No admins or supervisors found.
                </div>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded-lg border border-border" data-testid="js-team">
                  {staff.map((u) => (
                    <label key={u.id} className="flex min-h-[48px] cursor-pointer items-center gap-3 border-b border-border px-3 py-2 last:border-0">
                      <input
                        type="checkbox"
                        checked={form.team_ids.includes(u.id)}
                        onChange={() => toggleTeam(u.id)}
                        className="h-5 w-5 shrink-0 accent-[hsl(var(--primary))]"
                        data-testid={`js-team-${u.id}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{u.name}</span>
                        <span className="block text-xs text-muted-foreground">{u.role === "admin" ? "Admin" : "Supervisor"}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Links do projeto (por exemplo, a induction) */}
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">Project links</Label>
              <p className="mb-2 text-xs text-muted-foreground">Add the induction, or any link your crew needs for this project.</p>
              <div className="space-y-3">
                {form.links.map((l, i) => (
                  <div key={i} className="space-y-2 rounded-lg border border-border p-3" data-testid={`js-link-${i}`}>
                    <Input
                      value={l.title}
                      onChange={(e) => setLink(i, "title", e.target.value)}
                      placeholder="Name, e.g. Online Induction"
                      className={fieldClass}
                      data-testid={`js-link-title-${i}`}
                    />
                    <div className="flex gap-2">
                      <Input
                        value={l.url}
                        onChange={(e) => setLink(i, "url", e.target.value)}
                        placeholder="https://..."
                        inputMode="url"
                        autoCapitalize="none"
                        autoCorrect="off"
                        className={`${fieldClass} flex-1`}
                        data-testid={`js-link-url-${i}`}
                      />
                      <Button type="button" variant="outline" onClick={() => removeLink(i)} aria-label="Remove link" className="h-11 w-11 shrink-0 p-0 md:h-9 md:w-9" data-testid={`js-link-del-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addLink}
                disabled={form.links.length >= 12}
                className="mt-2 h-11 w-full rounded-lg md:h-9 md:rounded-sm"
                data-testid="js-link-add"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Add link
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={submit}
              disabled={saving || !form.name.trim() || !hasLocation}
              className="h-12 w-full rounded-lg bg-primary text-base hover:bg-primary/90 md:h-9 md:w-auto md:rounded-sm md:text-sm"
              data-testid="js-save-btn"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? "Saving..." : editing ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sites.length === 0 ? (
        <EmptyState title="No jobsites" body="Create your first site to enable clock in/out." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="jobsites-grid">
          {sites.map((s) => (
            <div key={s.id} className="border border-border bg-card rounded-sm p-5" data-testid={`jobsite-card-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-sm border border-border grid place-items-center text-primary">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-semibold">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.address || "—"}</div>
                  <div className="mt-2 text-[11px] font-mono text-muted-foreground">
                    {s.lat?.toFixed?.(5)}, {s.lng?.toFixed?.(5)} · geofence {s.radius_m}m
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => openEdit(s)} aria-label="Edit jobsite" data-testid={`js-edit-${s.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(s.id)} aria-label="Delete jobsite" data-testid={`js-del-${s.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
